import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { auth } from '../lib/api';
import { useSession } from '../lib/session';
import { useT } from '../lib/i18n';
import { useErrorText } from '../lib/errors';
import { ArrowRightIcon } from '../components/Icons';

type Mode = 'in' | 'up' | 'forgot' | 'reset';

/**
 * The one screen someone sees before they have an account.
 *
 * All four states live here rather than in four routes: they share a frame, a
 * form and most of their fields, and moving between them should not be a page
 * load. It is deliberately the only place in the app with no navigation —
 * there is nowhere to go yet.
 */
export function SignInPage() {
  const t = useT();
  const describe = useErrorText();
  const navigate = useNavigate();
  const { setUser, signupOpen, signupByList } = useSession();
  const [params] = useSearchParams();

  const resetToken = params.get('token') ?? '';
  // An invitation link opens straight onto the signup form.
  const inviteToken = params.get('invite') ?? '';
  const [mode, setMode] = useState<Mode>(resetToken ? 'reset' : inviteToken ? 'up' : 'in');
  const [email, setEmail] = useState('');
  const [terms, setTerms] = useState(false);
  /** What the invitation link opens: undefined while asking. */
  const [invite, setInvite] = useState<{ valid: boolean; email?: string } | undefined>();

  useEffect(() => {
    if (!inviteToken) return;
    let live = true;
    auth
      .checkInvite(inviteToken)
      .then((found) => {
        if (!live) return;
        setInvite(found);
        if (found.email) setEmail(found.email);
      })
      .catch(() => live && setInvite({ valid: false }));
    return () => {
      live = false;
    };
  }, [inviteToken]);

  /** Signup is offered when it is open to all, or this link opens it. */
  const canSignUp = signupOpen || signupByList || Boolean(invite?.valid);
  const lockedEmail = mode === 'up' && invite?.valid ? invite.email : undefined;
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (mode === 'in') {
        const { user } = await auth.login(email, password);
        setUser(user);
        navigate('/new', { replace: true });
      } else if (mode === 'up') {
        const result = await auth.signup({
          email,
          password,
          name,
          accountName: name,
          invite: inviteToken || undefined,
          acceptTerms: terms,
        });
        if (result.user) {
          setUser(result.user);
          navigate('/new', { replace: true });
        } else {
          // The address was already taken, which the server does not disclose.
          setSent(true);
        }
      } else if (mode === 'forgot') {
        await auth.forgot(email);
        setSent(true);
      } else {
        const { user } = await auth.reset(resetToken, password);
        setUser(user);
        navigate('/new', { replace: true });
      }
    } catch (err) {
      setError(describe(err, 'auth.failed'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Frame>
        <h1>{t('auth.checkMail')}</h1>
        <p className="muted">{t('auth.checkMailHint', { email })}</p>
        <button
          className="btn btn--block"
          onClick={() => {
            setSent(false);
            setMode('in');
          }}
        >
          {t('auth.backToSignIn')}
        </button>
      </Frame>
    );
  }

  const title =
    mode === 'in'
      ? 'auth.welcomeBack'
      : mode === 'up'
        ? 'auth.upTitle'
        : mode === 'forgot'
          ? 'auth.forgotTitle'
          : 'auth.resetTitle';

  return (
    <Frame>
      {/*
       * Signing in and opening a studio are the two doors, side by side. The
       * forgotten password and the reset are detours from the first one, so
       * they keep its tab lit rather than adding tabs of their own.
       */}
      {canSignUp && mode !== 'reset' && (
        <div className="auth__tabs" role="tablist" aria-label={t('auth.doors')}>
          <button
            type="button"
            role="tab"
            aria-selected={mode !== 'up'}
            className={mode !== 'up' ? 'is-on' : ''}
            onClick={() => setMode('in')}
          >
            {t('auth.signIn')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'up'}
            className={mode === 'up' ? 'is-on' : ''}
            onClick={() => setMode('up')}
          >
            {t('auth.createAccount')}
          </button>
        </div>
      )}
      <h1 className="auth__title">{t(title)}</h1>
      <p className="muted">
        {t(mode === 'up' ? (invite?.valid ? 'auth.invitedLede' : 'auth.createLede') : 'auth.signInLede')}
      </p>

      <form className="stack" onSubmit={submit}>
        {error && <div className="banner banner--error">{error}</div>}
        {mode === 'up' && inviteToken && invite && !invite.valid && (
          <div className="banner banner--error">{t('auth.inviteInvalid')}</div>
        )}
        {mode === 'up' && !canSignUp && !inviteToken && <div className="banner">{t('auth.inviteOnly')}</div>}
        {mode === 'up' && signupByList && !invite?.valid && <p className="faint">{t('auth.listOnly')}</p>}

        {mode === 'up' && (
          <div className="field">
            <label htmlFor="name">{t('auth.name')}</label>
            <input
              id="name"
              className="input input--line"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}

        {mode !== 'reset' && (
          <div className="field">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              className="input input--line"
              type="email"
              required
              autoComplete="email"
              autoFocus={!lockedEmail}
              readOnly={Boolean(lockedEmail)}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {lockedEmail && <p className="faint">{t('auth.inviteFor')}</p>}
          </div>
        )}

        {mode !== 'forgot' && (
          <div className="field">
            <label htmlFor="password">{t(mode === 'reset' ? 'auth.newPassword' : 'auth.password')}</label>
            <input
              id="password"
              className="input input--line"
              type="password"
              required
              minLength={mode === 'in' ? undefined : 10}
              autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
              autoFocus={mode === 'reset'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {mode !== 'in' && <p className="faint">{t('auth.passwordHint')}</p>}
          </div>
        )}

        {mode === 'up' && (
          <label className="check">
            <input type="checkbox" required checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span>
              {t('auth.acceptBefore')}{' '}
              <a href="/terms" target="_blank" rel="noreferrer">
                {t('legal.terms')}
              </a>{' '}
              {t('auth.acceptAnd')}{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">
                {t('legal.privacy')}
              </a>
              .
            </span>
          </label>
        )}

        <div className="auth__actions">
          <button
            className="auth__go"
            type="submit"
            disabled={busy || (mode === 'up' && (!canSignUp || !terms))}
          >
            {t(
              busy
                ? 'auth.working'
                : mode === 'in'
                  ? 'auth.signIn'
                  : mode === 'up'
                    ? 'auth.createAccount'
                    : mode === 'forgot'
                      ? 'auth.sendLink'
                      : 'auth.setPassword',
            )}
            <span className="auth__goDot" aria-hidden="true">
              {busy ? <span className="spinner" /> : <ArrowRightIcon />}
            </span>
          </button>
          {mode === 'in' && (
            <button type="button" className="auth__link" onClick={() => setMode('forgot')}>
              {t('auth.forgot')}
            </button>
          )}
          {(mode === 'forgot' || (mode === 'up' && !canSignUp)) && (
            <button type="button" className="auth__link" onClick={() => setMode('in')}>
              {t('auth.haveAccount')}
            </button>
          )}
        </div>
      </form>

      {mode === 'in' && !canSignUp && <p className="faint auth__note">{t('auth.inviteOnly')}</p>}
    </Frame>
  );
}

/**
 * The door: a block of ink, and the form beside it.
 *
 * The left half says what the studio does, three verbs taking turns in type
 * as large as the screen allows; the right half is the work, with nothing
 * around it but white. On a phone the block becomes a band across the top and
 * the form follows it, because the verbs are the only introduction this
 * screen gives and a phone deserves it as much as a desk.
 */
function Frame({ children }: { children: React.ReactNode }) {
  const t = useT();
  return (
    <div className="auth">
      <aside className="auth__art">
        <span className="auth__brand">
          studio<span>.</span>
        </span>
        <div className="auth__pitch">
          <p className="auth__cycle">
            <span className="sr-only">{[t('auth.word1'), t('auth.word2'), t('auth.word3')].join(' ')}</span>
            <span className="auth__words" aria-hidden="true">
              <span>{t('auth.word1')}</span>
              <span>{t('auth.word2')}</span>
              <span>{t('auth.word3')}</span>
              <span>{t('auth.word1')}</span>
            </span>
          </p>
          <p className="auth__artLine">{t('auth.artLine')}</p>
        </div>
      </aside>

      <main className="auth__form">{children}</main>
    </div>
  );
}
